import type { IReactionDisposer } from 'mobx';
import { reaction } from 'mobx';
import type { IDisposable } from '@/engine/Utilities/IDisposable';

import type { IMeasurementViewModel, IObjectViewModel } from './interfaces';
import type { IVisibilityDependency } from './models/viewEntity/view.entity.ts';
import type { ViewsService } from './Views.service';

export class ViewItemsVisibilityManager implements IDisposable {
  private _viewsService: ViewsService;

  private _visibilityDependencies: Array<IVisibilityDependency>;

  private _disposers: Array<IReactionDisposer> = [];

  private _viewItems: Array<IObjectViewModel> = [];

  public constructor(viewsService: ViewsService, visibilityDependencies: Array<IVisibilityDependency>) {
    this._viewsService = viewsService;
    this._visibilityDependencies = visibilityDependencies;

    const objects: Array<IObjectViewModel> | undefined = this._viewsService.currentView?.getObjects();
    const measurements: Array<IMeasurementViewModel> | undefined = this._viewsService.currentView?.getMeasurements();

    if (objects) {
      this._viewItems = this._viewItems.concat(objects);
    }

    if (measurements) {
      this._viewItems = this._viewItems.concat(measurements);
    }

    this._visibilityDependencies.forEach((dependency: IVisibilityDependency): void => {
      const dependentItem: IObjectViewModel | undefined = this._geViewItemByDescriptor(dependency.item);

      if (!dependentItem) {
        throw new Error('Unknown dependent item descriptor');
      }

      const dependsOnItems: Array<IObjectViewModel> = [];

      dependency.dependsOn.forEach((depOnDescriptor: string): void => {
        const depOnItem: IObjectViewModel | undefined = this._geViewItemByDescriptor(depOnDescriptor);

        if (!depOnItem) {
          return;
        }

        dependsOnItems.push(depOnItem);

        const vmVisibilityReactionDisposer: IReactionDisposer = reaction(
          () => depOnItem.isVisible,
          (/*visibility*/) =>
            dependentItem.setVisibility(dependsOnItems.some((item: IObjectViewModel) => item.isVisible))
        );

        const vmHighlightingReactionDisposer: IReactionDisposer = reaction(
          () => depOnItem.isHighlighted,
          (/*highlighting*/) =>
            dependentItem.setHighlighting(dependsOnItems.some((item: IObjectViewModel) => item.isHighlighted))
        );

        this._disposers.push(vmVisibilityReactionDisposer, vmHighlightingReactionDisposer);
      });
    });
  }

  private _geViewItemByDescriptor(descriptor: string): IObjectViewModel | undefined {
    return this._viewItems.find((item: IObjectViewModel): boolean => item.descriptor === descriptor);
  }

  public dispose(): void {
    this._disposers.forEach((disposer: IReactionDisposer) => disposer());
  }
}
